import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "labelInput", "fieldType", "fieldTypeLabel",
    "preferencesHeader", "listSelectGroup", "showInListGroup",
    "assetLinkGroup", "numberGroup", "addressGroup",
    "dateGroup", "requiredGroup", "dropdownGroup",
    "hintGroup", "multipleOptions", "radarGroup", 
    "sourcesHeader", "integratorSlugSelector", "integratorSlugGroup",
    "integratorDataPointGroup", "integratorDataPointSelector",
    "radarSelector", "multipleOptionsGroup", "radarDateGroup"
  ]

  static values = {
    lists: Array,
    assetTypes: Array,
    mapboxIntegration: Boolean,
    companyStructureName: String
  }

  connect() {
    this.updateFieldTypeDisplay()
    if (this.integratorSlugSelectorTarget.value) {
      this.fetchDataPoints()
    }
    this.toggleIntegratorSlugGroup()
  }

  toggleExpiration(event) {
    if(this.hasRadarDateGroupTarget) {
      const expirationUnchecked = !event.target.checked;
      this.radarDateGroupTarget.classList.toggle("hidden", expirationUnchecked)
      
      // Reset back to false when expiration has been unchecked
      if(expirationUnchecked) {
        const radarDateGroupCheck = this.radarDateGroupTarget.querySelector("input[type=checkbox]");
        
        radarDateGroupCheck.checked = false;
      }
    }
  }

  changeFieldType(event) {
    const selectedType = event.target.value
    this.fieldTypeTarget.value = selectedType
    this.updateFieldTypeDisplay()
    this.integratorSlugSelectorTarget.value = null
    this.integratorDataPointSelectorTarget.value = null
    this.integratorDataPointGroupTarget.classList.add("hidden")
  }

  updateFieldTypeDisplay() {
    const currentType = this.fieldTypeTarget.value

    // Update selected state of field type labels
    this.fieldTypeLabelTargets.forEach(label => {
      const fieldType = label.dataset.fieldType
      label.classList.toggle("selected", fieldType === currentType)
    })

    // Show/hide preference sections based on type
    this.preferencesHeaderTarget.classList.toggle("hidden", currentType === "Heading")

    this.listSelectGroupTarget.classList.toggle("hidden", currentType !== "ListSelect")

    this.multipleOptionsGroupTarget.classList.toggle("hidden", currentType !== "ListSelect")

    this.showInListGroupTarget.classList.toggle("hidden",
      ["Heading", "RichText", "Embed"].includes(currentType))

    this.assetLinkGroupTarget.classList.toggle("hidden",
      !["AssetLink", "AssetTag"].includes(currentType))

    this.numberGroupTarget.classList.toggle("hidden", currentType !== "Number")

    this.addressGroupTarget.classList.toggle("hidden",
      currentType !== "AddressData" || !this.mapboxIntegrationValue)

    this.dateGroupTarget.classList.toggle("hidden", currentType !== "Date")

    this.requiredGroupTarget.classList.toggle("hidden",
      ["Heading", "CheckBox"].includes(currentType))

    this.dropdownGroupTarget.classList.toggle("hidden", currentType !== "Dropdown")

    this.hintGroupTarget.classList.toggle("hidden", currentType === "Heading")

    if (this.hasIntegratorSlugGroupTarget) {
      this.integratorSlugGroupTarget.classList.toggle("hidden", !["Text", "RichText", "Email", "Phone"].includes(currentType))
    }

    if (this.hasRadarGroupTarget) {
      this.radarGroupTarget.classList.toggle("hidden", !["Text", "RichText"].includes(currentType))
    }

    if(this.hasRadarDateGroupTarget) {
      const dateGroupCheck = this.dateGroupTarget.querySelector("input[type=checkbox]");
      this.radarDateGroupTarget.classList.toggle("hidden", currentType !== "Date" || !dateGroupCheck.checked);
    }

    if (
      ['AssetLink', 'AssetTag', 'ListSelect', 'Dropdown'].includes(currentType) || 
      (this.hasRadarSelectorTarget && ['Text', 'RichText'].includes(currentType) ) ||
      (this.hasIntegratorSlugGroupTarget && ['Text', 'RichText', 'Email', 'Phone'].includes(currentType))
    ){
      this.sourcesHeaderTarget.classList.remove("hidden");
    } else {
      this.sourcesHeaderTarget.classList.add("hidden");
    }
  }

  toggleIntegratorSlugGroup() {
    if (!this.hasRadarSelectorTarget) {
      return;
    }

    if (!this.hasIntegratorSlugGroupTarget) {
      return;
    }

    const radar = this.radarSelectorTarget.value;
    if(radar) {
      this.integratorSlugGroupTarget.classList.add("disabled");
      return
    }
    this.integratorSlugGroupTarget.classList.remove("disabled");
  }

  fetchDataPoints() {
    const selectedOption = this.integratorSlugSelectorTarget.selectedOptions[0];
    const integratorId = selectedOption?.dataset.id;
    const fieldType = this.fieldTypeTarget ? this.fieldTypeTarget.value : null;

    if (!integratorId) {
      this.integratorDataPointGroupTarget.classList.add("hidden");
      this.integratorDataPointGroupTarget.classList.remove("required");
      this.integratorDataPointSelectorTarget.innerHTML = '<option value="">Select an option</option>';
      if (this.radarGroupTarget) {
        this.radarGroupTarget.classList.remove("disabled");
      }
      return;
    }

    if (this.hasRadarGroupTarget) {
      this.radarGroupTarget.classList.add("disabled");
    }

    const csrfToken = document.querySelector("meta[name='csrf-token']").getAttribute("content");

    fetch(`/admin/integrators/${integratorId}/data_fill_fields`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken, // Include CSRF token for Rails
      },
      body: JSON.stringify({ type: fieldType }) // Send `type` as JSON payload
    })
      .then(response => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then(data => {

        this.updateDataPointDropdown(data);
      })
      .catch(error => console.error("Error fetching data points:", error));  
  }

  updateDataPointDropdown(data) {
    this.integratorDataPointSelectorTarget.innerHTML = '<option value="">Select an option</option>';
    let existingDataPoint = this.integratorDataPointSelectorTarget.dataset.selectedValue;

    let dataPoints = data["fields"]

    dataPoints.forEach(dataPoint => {
      let option = document.createElement("option");
      option.value = dataPoint;
      option.textContent = dataPoint;

      if (existingDataPoint && dataPoint === existingDataPoint) {
        option.selected = true;
      }

      this.integratorDataPointSelectorTarget.appendChild(option);
    });

    this.integratorDataPointGroupTarget.classList.remove("hidden");
    this.integratorDataPointGroupTarget.classList.add("required");
  }

  updateListId(event) {
    // Handle list selection change if needed
  }

  updateLinkableId(event) {
    // Handle asset type selection change if needed
  }
}
